import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_DASHBOARD_RESOURCE_USAGE_QUERY_KEY =
  'cluster-dashboard-resource-usage';

const getResourceUsageQueryKey = ({
  clusterId,
  startTime,
  endTime,
  stepSeconds,
  cacheKeySuffix,
}: {
  clusterId: string;
  startTime: string;
  endTime: string;
  stepSeconds?: number;
  cacheKeySuffix?: string | null;
}) =>
  [
    CLUSTER_DASHBOARD_RESOURCE_USAGE_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    stepSeconds,
    cacheKeySuffix ?? null,
  ] as const;

export type ResourceUsageQueryParameters = {
  clusterId: string;
  startTime: string;
  endTime: string;
  stepSeconds?: number;
  enabled?: boolean;
  cacheKeySuffix?: string | null;
  refetchInterval?: (data: unknown) => number | false;
};

export const getResourceUsageQueryOptions = (
  {
    clusterId,
    startTime,
    endTime,
    stepSeconds,
    enabled = true,
    cacheKeySuffix,
    refetchInterval,
  }: ResourceUsageQueryParameters,
  client: ReturnType<typeof useQueryClient>
) =>
  queryOptions({
    queryKey: getResourceUsageQueryKey({
      clusterId,
      startTime,
      endTime,
      stepSeconds,
      cacheKeySuffix,
    }),
    queryFn: async () => {
      return apiClient.costReport
        .getResourceUsage({ clusterId, startTime, endTime, stepSeconds })
        .then((resp) => resp.data?.items || []);
    },
    enabled,
    refetchInterval: ({ queryKey }) => {
      if (refetchInterval) {
        return refetchInterval(client.getQueryData(queryKey));
      }
      return false;
    },
  });

export const useResourceUsageQuery = (
  parameters: ResourceUsageQueryParameters
) => {
  const client = useQueryClient();

  const {
    data: resourceUsage,
    isLoading,
    error,
    refetch,
  } = useQuery(getResourceUsageQueryOptions(parameters, client));

  return {
    resourceUsage: resourceUsage || undefined,
    isLoading,
    error,
    refetch,
  };
};
